import { useEffect, useRef, useState } from "react";
import { PublicFooter } from "@/components/Footer";
import api, { formatApiError } from "@/lib/api";
import { PromoRibbon } from "@/components/PromoRibbon";
import { logger } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Loader2, UploadCloud, CheckCircle2, AlertTriangle, ArrowRight } from "lucide-react";
import { Logo } from "@/components/Logo";

const ROLES = ["Software Engineer","Senior Software Engineer","Data Scientist","Data Analyst","Analytics Engineer","Product Manager","Project Manager","Marketing Manager","Digital Marketing Specialist","Sales Executive","Account Manager","Business Analyst","Financial Analyst","Accountant","HR Manager","Recruiter","Operations Manager","Customer Success Manager","UX Designer","UI Designer","Graphic Designer","DevOps Engineer","Cloud Engineer","Cybersecurity Analyst","Machine Learning Engineer","Frontend Developer","Backend Developer","Full Stack Developer","Nurse","Teacher","Mechanical Engineer","Civil Engineer","Electrical Engineer","Consultant","Solicitor","Registered Nurse","Care Assistant","Supply Chain Manager","Quantity Surveyor","Administrative Assistant"];
const SENIORITY = ["Entry","Mid","Senior","Leadership"];
const STAGES = ["Parsing your CV…", "Scanning against live listings…", "Checking ATS compatibility…", "Scoring…"];

const band = (s) => (s < 51 ? { color: "#dc2626", label: "Needs work" } : s <= 75 ? { color: "#d97706", label: "Getting there" } : s < 85 ? { color: "#2563eb", label: "Very good" } : { color: "#16a34a", label: "Strong" });

function Gauge({ score }) {
  const [shown, setShown] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const dur = 1200;
    const tick = (now) => {
      const t = Math.min((now - start) / dur, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setShown(Math.round(eased * score));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [score]);
  const b = band(shown);
  const r = 70, c = 2 * Math.PI * r, off = c - (shown / 100) * c;
  return (
    <div className="relative w-48 h-48 mx-auto" data-testid="score-gauge">
      <svg width="192" height="192" viewBox="0 0 192 192" className="-rotate-90">
        <circle cx="96" cy="96" r={r} fill="none" stroke="#e5e7eb" strokeWidth="14" />
        <circle cx="96" cy="96" r={r} fill="none" stroke={b.color} strokeWidth="14" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-heading text-5xl font-bold" style={{ color: b.color }}>{shown}%</span>
        <span className="text-xs font-semibold mt-1" style={{ color: b.color }}>{b.label}</span>
      </div>
    </div>
  );
}

function ProgressRing({ value }) {
  const v = Math.min(Math.max(Math.round(value), 0), 100);
  const done = v >= 100;
  const color = done ? "#16a34a" : "#24468C";
  const r = 70, c = 2 * Math.PI * r, off = c - (v / 100) * c;
  return (
    <div className="relative w-44 h-44 mx-auto" data-testid="cva-progress-ring">
      <svg width="176" height="176" viewBox="0 0 192 192" className="-rotate-90">
        <circle cx="96" cy="96" r={r} fill="none" stroke="#e5e7eb" strokeWidth="12" />
        <circle cx="96" cy="96" r={r} fill="none" stroke={color} strokeWidth="12" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={off} style={{ transition: "stroke-dashoffset 0.25s ease, stroke 0.3s ease" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        {done
          ? <CheckCircle2 className="h-12 w-12 text-[#16a34a]" strokeWidth={1.75} />
          : <span className="font-heading text-4xl font-bold text-[#24468C]" data-testid="cva-progress-pct">{v}%</span>}
      </div>
    </div>
  );
}

function Bar({ label, value }) {
  const b = band(value);
  return (
    <div data-testid={`subscore-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
      <div className="flex justify-between text-sm mb-1"><span className="text-muted-foreground">{label}</span><span className="font-semibold">{value}%</span></div>
      <div className="h-2 rounded-full bg-zinc-200 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${value}%`, background: b.color, transition: "width 1s ease" }} /></div>
    </div>
  );
}

export default function FreeCvAnalysis() {
  const params = new URLSearchParams(window.location.search);
  const [phase, setPhase] = useState("form"); // form | processing | results | fallback
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [seniority, setSeniority] = useState("");
  const [file, setFile] = useState(null);
  const [fileErr, setFileErr] = useState("");
  const [aiConsent, setAiConsent] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [drag, setDrag] = useState(false);
  const [stage, setStage] = useState(0);
  const [progress, setProgress] = useState(0);
  const [retrying, setRetrying] = useState(false);
  const [result, setResult] = useState(null);
  const [leadId, setLeadId] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    api.post("/free-analysis/view", { utm: params.toString() }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pickFile = (f) => {
    setFileErr("");
    if (!f) return;
    const ok = /\.(pdf|docx)$/i.test(f.name);
    if (!ok) { setFileErr("Only PDF or DOCX files are accepted."); return; }
    if (f.size > 5 * 1024 * 1024) { setFileErr("File must be under 5 MB."); return; }
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    setFileErr("");
    if (!file) { setFileErr("Please upload your CV."); return; }
    if (!aiConsent) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", name); fd.append("email", email); fd.append("target_role", role);
      fd.append("seniority", seniority); fd.append("ai_consent", "true");
      fd.append("marketing_consent", marketing ? "true" : "false");
      fd.append("utm", params.toString()); fd.append("file", file);
      const { data } = await api.post("/free-analysis/submit", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLeadId(data.lead_id);
      setPhase("processing");
    } catch (err) {
      setFileErr(formatApiError(err.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (phase !== "processing" || !leadId) return;
    let elapsed = 0, done = false;
    setProgress(3);
    // Smoothly ramp progress toward ~92% while the background job runs; it never
    // completes on its own — it snaps to 100% only when the analysis is truly done.
    const ramp = setInterval(() => {
      setProgress((p) => (p >= 94 ? p : p + (94 - p) * 0.012));
    }, 250);
    const cleanup = () => { clearInterval(poll); clearInterval(ramp); };
    const complete = (data) => {
      clearInterval(ramp); clearInterval(poll);
      setProgress(100);
      setResult(data);
      setTimeout(() => setPhase("results"), 550); // let the ring visibly finish
    };
    const poll = setInterval(async () => {
      elapsed += 3;
      try {
        const { data } = await api.get(`/free-analysis/leads/${leadId}`);
        if (data.status === "done") { done = true; complete(data); }
        else if (data.status === "failed") { done = true; setPhase("failed"); cleanup(); }
        else if (data.status === "queued") { done = true; setPhase("fallback"); cleanup(); }
      } catch (e) { logger.debug("analysis status poll retry", e?.message); /* keep polling */ }
      if (!done && elapsed >= 300) { setPhase("fallback"); cleanup(); }
    }, 3000);
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, leadId]);

  const trialLink = `/signup?lead=${leadId || ""}&name=${encodeURIComponent(name)}&email=${encodeURIComponent(email)}&role=${encodeURIComponent(role)}`;

  const retry = async () => {
    if (!leadId) return;
    setRetrying(true);
    try {
      await api.post(`/free-analysis/leads/${leadId}/retry`);
      setStage(0);
      setPhase("processing");
    } catch (err) {
      setFileErr(formatApiError(err.response?.data?.detail) || "Couldn't retry. Please submit a new analysis.");
      setPhase("form");
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-50 to-white">
      <PromoRibbon />
      <header className="border-b border-zinc-200 bg-white/70 backdrop-blur">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center gap-2">
          <Logo imgClassName="h-8" />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-12">
        {phase === "form" && (
          <>
            <div className="text-center mb-8">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-primary bg-gold/15 px-3 py-1 rounded-full"><Sparkles className="h-3.5 w-3.5 text-gold" /> Free Instant CV Analysis</span>
              <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mt-4">Let's analyse your CV in 60 seconds</h1>
              <p className="text-muted-foreground mt-3">Get an instant ATS score, role-fit breakdown, and your top fixes — free.</p>
            </div>
            <form onSubmit={submit} className="bg-white border border-zinc-200 rounded-xl shadow-sm p-6 sm:p-8 space-y-5" data-testid="cv-analysis-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label className="text-xs font-semibold uppercase tracking-wider">Full name *</Label><Input required value={name} onChange={(e) => setName(e.target.value)} data-testid="cva-name" /></div>
                <div className="space-y-1.5"><Label className="text-xs font-semibold uppercase tracking-wider">Email *</Label><Input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="cva-email" /></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-wider">Target job role *</Label>
                  <Input required list="role-list" value={role} onChange={(e) => setRole(e.target.value)} placeholder="e.g. Data Analyst" data-testid="cva-role" />
                  <datalist id="role-list">{ROLES.map((r) => <option key={r} value={r} />)}</datalist>
                  <p className="text-xs text-muted-foreground">Pick from the list or type your own role — including "Other" roles not listed.</p>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-wider">Seniority</Label>
                  <Select value={seniority} onValueChange={setSeniority}>
                    <SelectTrigger data-testid="cva-seniority"><SelectValue placeholder="Optional" /></SelectTrigger>
                    <SelectContent>{SENIORITY.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider">Your CV *</Label>
                <div
                  onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                  onDragLeave={() => setDrag(false)}
                  onDrop={(e) => { e.preventDefault(); setDrag(false); pickFile(e.dataTransfer.files?.[0]); }}
                  onClick={() => fileRef.current?.click()}
                  className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${drag ? "border-accent bg-accent/5" : "border-zinc-300 hover:border-zinc-400"}`}
                  data-testid="cva-dropzone"
                >
                  <UploadCloud className="h-8 w-8 mx-auto text-zinc-400" strokeWidth={1.25} />
                  <p className="text-sm mt-2">{file ? <span className="font-medium">{file.name}</span> : "Drag & drop or click to upload (PDF or DOCX, max 5 MB)"}</p>
                  <input ref={fileRef} type="file" accept=".pdf,.docx" className="hidden" onChange={(e) => pickFile(e.target.files?.[0])} data-testid="cva-file-input" />
                </div>
                {fileErr && <p className="text-sm text-destructive" data-testid="cva-file-error">{fileErr}</p>}
              </div>

              <label className="flex items-start gap-2 cursor-pointer text-sm">
                <Checkbox checked={aiConsent} onCheckedChange={(v) => setAiConsent(!!v)} className="mt-0.5" data-testid="cva-ai-consent" />
                <span>I agree to the <a href="/terms" target="_blank" rel="noreferrer" className="underline text-[#24468C]" data-testid="cva-terms-link">Terms &amp; Conditions</a>. *</span>
              </label>
              <label className="flex items-start gap-2 cursor-pointer text-sm">
                <Checkbox checked={marketing} onCheckedChange={(v) => setMarketing(!!v)} className="mt-0.5" data-testid="cva-marketing-consent" />
                <span>Email me job search tips and product updates.</span>
              </label>

              <Button type="submit" disabled={submitting || !aiConsent} className="w-full h-12 text-base active:scale-[0.98] transition-transform" data-testid="cva-submit">
                {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : "Analyse my CV — free"}
              </Button>
              <p className="text-xs text-center text-muted-foreground">Your CV is never shared and is deleted after 30 days. <a href="/privacy" className="underline">Privacy</a></p>
            </form>
          </>
        )}

        {phase === "processing" && (
          <div className="text-center py-16" data-testid="cva-processing">
            <ProgressRing value={progress} />
            <p className="font-heading text-2xl font-bold mt-6">{(progress >= 100 ? "Analysis complete!" : STAGES[progress < 25 ? 0 : progress < 55 ? 1 : progress < 82 ? 2 : 3]).replace("live listings", `live ${role || "role"} listings`)}</p>
            <p className="text-muted-foreground text-sm mt-2">
              {progress >= 100 ? "Preparing your results…" : `Analysing your CV against real ${role || "role"} expectations…`}
            </p>
          </div>
        )}

        {phase === "fallback" && (
          <div className="text-center py-20" data-testid="cva-fallback">
            <AlertTriangle className="h-12 w-12 mx-auto text-amber-500" strokeWidth={1.5} />
            <p className="font-heading text-2xl font-bold mt-6">This is taking longer than expected</p>
            <p className="text-muted-foreground mt-2">We'll email your full report to <span className="font-medium">{email}</span> shortly.</p>
          </div>
        )}

        {phase === "failed" && (
          <div className="text-center py-20" data-testid="cva-failed">
            <AlertTriangle className="h-12 w-12 mx-auto text-red-500" strokeWidth={1.5} />
            <p className="font-heading text-2xl font-bold mt-6">We couldn't complete your analysis</p>
            <p className="text-muted-foreground mt-2">Something went wrong while analysing your CV. Please try again.</p>
            <Button onClick={retry} disabled={retrying} className="mt-6 h-12 px-8 text-base active:scale-[0.98] transition-transform" data-testid="cva-retry-btn">
              {retrying ? <Loader2 className="h-5 w-5 animate-spin" /> : "Try again"}
            </Button>
          </div>
        )}

        {phase === "results" && result?.summary && (
          <div className="space-y-8" data-testid="cva-results">
            <div className="text-center">
              <h1 className="font-heading text-3xl font-bold tracking-tight">Your CV Score</h1>
              <p className="text-muted-foreground text-sm mt-1">Targeting {result.target_role}</p>
            </div>
            <Gauge score={result.summary.overall_score} />
            {result.summary.overall_score < 85 && (
              <div className="max-w-lg mx-auto -mt-2 bg-accent/5 border border-accent/20 rounded-lg p-4 text-center" data-testid="cva-upsell">
                <p className="text-sm text-foreground">Your CV scores below our <strong>Strong (85+)</strong> benchmark for {result.target_role}. <strong>Jobsmith</strong> can optimise it to align perfectly with any job description in seconds.</p>
                <a href={trialLink} data-testid="cva-upsell-cta">
                  <Button size="sm" className="mt-3 active:scale-[0.98] transition-transform">Customise my CV — free 7-day trial <ArrowRight className="h-4 w-4 ml-1.5" strokeWidth={1.5} /></Button>
                </a>
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-4 max-w-lg mx-auto">
              <Bar label="ATS Compatibility" value={result.summary.sub_scores.ats} />
              <Bar label="Role Alignment" value={result.summary.sub_scores.role_alignment} />
              <Bar label="Impact & Evidence" value={result.summary.sub_scores.impact} />
              <Bar label="Structure & Readability" value={result.summary.sub_scores.structure} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4" data-testid="cva-strengths">
                <p className="font-semibold text-green-800 flex items-center gap-1.5 mb-2"><CheckCircle2 className="h-4 w-4" /> What's working</p>
                <ul className="space-y-1.5 text-sm text-green-900">{result.summary.strengths.map((s, i) => <li key={`strength-${i}`}>• {s}</li>)}</ul>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-testid="cva-weaknesses">
                <p className="font-semibold text-red-800 flex items-center gap-1.5 mb-2"><AlertTriangle className="h-4 w-4" /> What's holding you back</p>
                <ul className="space-y-1.5 text-sm text-red-900">{result.summary.weaknesses.map((s, i) => <li key={`weakness-${i}`}>• {s}</li>)}</ul>
              </div>
            </div>
            <div className="bg-accent/5 border border-accent/20 rounded-lg p-4 text-center text-sm">
              Your full report — including your <strong>top 5 fixes</strong> and a <strong>rewritten example</strong> from your own CV — is on its way to <strong>{result.email}</strong>. Check spam if it hasn't arrived in 2 minutes.
            </div>
            <div className="text-center">
              <p className="font-heading text-xl font-bold tracking-tight mb-1">Let's customise your CV for every job application</p>
              <p className="text-sm text-muted-foreground mb-4">AI-tailored CVs · instant cover letters · 5 job matches a day · application tracker</p>
              <a href={trialLink} data-testid="cva-trial-cta">
                <Button className="h-12 px-8 text-base active:scale-[0.98] transition-transform">Start my free 7-day trial <ArrowRight className="h-4 w-4 ml-1.5" strokeWidth={1.5} /></Button>
              </a>
              <p className="text-xs text-muted-foreground mt-2">No card required · cancel anytime</p>
            </div>
          </div>
        )}
      </main>
      <PublicFooter />
    </div>
  );
}
